/**
 * @file UploadAutomationDialog.jsx
 * @description Add / edit dialog for a file-drop automation. Picks the
 *              language, the slot in the daily drop, the campaign the file
 *              feeds, and which of that campaign's schedules may fire.
 *              Windows are entered in the client time zone (Configuration →
 *              Time Settings), surfaced next to the time fields.
 * @component UploadAutomationDialog
 * @exports UploadAutomationDialog
 */

import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem,
    Select, Switch, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
    AUTOMATION_STRINGS, DAY_OF_WEEK_OPTIONS, DAY_KEYS, DAY_PRESETS,
    LANGUAGE_OPTIONS, NEW_CONFIG,
    MIN_UPLOAD_ORDER, MAX_UPLOAD_ORDER,
    MAX_NAME, MAX_DESCRIPTION, MAX_FILE_EXAMPLE, EMAIL_RE,
    describeConfig, describeDays, resolveTimeZone, formatClock,
} from "../../common/constants/uploadAutomationModule/uploadAutomationConstants.js";
import {
    fetchAutomationCampaigns, fetchCampaignSchedules,
} from "../../services/uploadAutomationModule/uploadAutomationServices.js";
import {
    inferFileFormat, renderFileName, nextRunAt, describeFormat,
} from "./fileNamePattern.js";

const BLUE  = "#378ADD";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const RUN_DATE_FMT = { weekday: "short", month: "short", day: "numeric" };

// ── Small building blocks ──────────────────────────────────────────
function Field({ label, hint, required, children }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75,
            }}>
                {label}{required && <Box component="span" sx={{ color: PINK, ml: 0.3 }}>*</Box>}
            </Typography>
            {children}
            {hint && (
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
}

/** Pill segmented control — same visual language as the report dialog. */
function Segmented({ options, value, onChange, disabledValues = [] }) {
    return (
        <Box sx={{
            display: "inline-flex", flexWrap: "wrap",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: "20px", overflow: "hidden",
        }}>
            {options.map(o => {
                const on  = o.value === value;
                const off = disabledValues.includes(o.value) && !on;
                return (
                    <Box
                        key={o.value}
                        onClick={off ? undefined : () => onChange(o.value)}
                        title={off ? AUTOMATION_STRINGS.ORDER_TAKEN : o.hint}
                        sx={{
                            px: "16px", py: "6px", fontSize: 13,
                            fontWeight: on ? 500 : 400,
                            cursor: off ? "not-allowed" : "pointer",
                            userSelect: "none",
                            background: on ? BLUE : "transparent",
                            color: on ? "#fff" : (off ? "text.disabled" : "text.secondary"),
                            textDecoration: off ? "line-through" : "none",
                            transition: "all .15s",
                            "&:hover": (on || off) ? {} : { background: "rgba(55,138,221,0.06)" },
                        }}
                    >{o.label}</Box>
                );
            })}
        </Box>
    );
}

/** Multi-select weekday picker — a drop can land on any mix of days. */
function DayPicker({ value = [], onChange }) {
    const toggle = (day) => onChange(
        value.includes(day)
            ? value.filter(d => d !== day)
            : DAY_KEYS.filter(d => d === day || value.includes(d))   // keep week order
    );

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
                {DAY_OF_WEEK_OPTIONS.map(d => {
                    const on = value.includes(d.value);
                    return (
                        <Box key={d.value} onClick={() => toggle(d.value)} title={d.full} sx={{
                            width: 42, py: "6px", textAlign: "center",
                            fontSize: 12, fontWeight: on ? 500 : 400,
                            borderRadius: "16px", cursor: "pointer", userSelect: "none",
                            border: "0.5px solid",
                            borderColor: on ? BLUE : "divider",
                            background: on ? BLUE : "transparent",
                            color: on ? "#fff" : "text.secondary",
                            transition: "all .15s",
                            "&:hover": on ? {} : { background: "rgba(55,138,221,0.06)" },
                        }}>{d.label}</Box>
                    );
                })}
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                {DAY_PRESETS.map(p => (
                    <Box key={p.label} onClick={() => onChange([...p.days])} sx={{
                        fontSize: 11, color: BLUE, cursor: "pointer", userSelect: "none",
                        "&:hover": { textDecoration: "underline" },
                    }}>{p.label}</Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Renders "VEROS_{YYYYMMDD}_{SEQ:2}.csv" with the variable parts picked out,
 * so it's obvious at a glance which characters are allowed to change.
 */
function highlightTemplate(template) {
    return String(template || "").split(/(\{[^}]+\})/).map((chunk, i) => (
        chunk.startsWith("{") && chunk.endsWith("}") ? (
            <Box key={i} component="span" sx={{
                color: BLUE, background: "rgba(55,138,221,0.10)",
                borderRadius: "3px", px: "3px",
            }}>{chunk}</Box>
        ) : (
            <Box key={i} component="span">{chunk}</Box>
        )
    ));
}

/** Days a campaigntextmsg already sends on, as a quiet inline hint. */
const scheduleHint = (s) => {
    if (!s.days?.length) return "No send days set";
    const first = s.times?.[s.days[0]];
    return `${describeDays(s.days)}${first ? ` · ${formatClock(first)}` : ""}`;
};

// ── Component ──────────────────────────────────────────────────────
const UploadAutomationDialog = ({
    open, config, tzKey, saving, takenOrders = {}, onClose, onSave,
}) => {
    const [draft,       setDraft]       = useState({ ...NEW_CONFIG });
    const [campaigns,   setCampaigns]   = useState([]);
    const [schedules,   setSchedules]   = useState([]);
    const [loadingCamp, setLoadingCamp] = useState(false);
    const [loadingSch,  setLoadingSch]  = useState(false);
    const [formError,   setFormError]   = useState("");
    const [touchedCampaign, setTouchedCampaign] = useState(false);
    const [emailInput,  setEmailInput]  = useState("");
    const [emailError,  setEmailError]  = useState("");

    const isEdit = !!draft.configId;
    const set    = (patch) => { setDraft(d => ({ ...d, ...patch })); setFormError(""); };
    const tz     = resolveTimeZone(tzKey);

    useEffect(() => {
        setDraft({ ...NEW_CONFIG, ...(config || {}) });
        setFormError(""); setTouchedCampaign(false);
        setEmailInput(""); setEmailError("");
    }, [config]);

    // Campaigns follow the language — an EN drop can't feed a Spanish campaign.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoadingCamp(true);
                const list = await fetchAutomationCampaigns(draft.language);
                if (cancelled) return;
                setCampaigns(list);
                // Only clear the campaign once the operator has actually
                // switched language — never on the initial load of an edit.
                if (touchedCampaign && draft.campaignId &&
                    !list.some(c => String(c.campaignId) === String(draft.campaignId))) {
                    setDraft(d => ({ ...d, campaignId: "", scheduleIds: [] }));
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) setCampaigns([]);
            } finally {
                if (!cancelled) setLoadingCamp(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft.language]);

    // Schedules follow the campaign. Inactive ones are still listed so an
    // existing link never disappears without the operator seeing it.
    useEffect(() => {
        let cancelled = false;
        if (!draft.campaignId) { setSchedules([]); return undefined; }
        (async () => {
            try {
                setLoadingSch(true);
                const all = await fetchCampaignSchedules(draft.campaignId);
                if (cancelled) return;
                // Inactive schedules aren't offered — an automation shouldn't
                // be able to point at one that can't fire. A previously linked
                // schedule that has since been paused therefore drops out of
                // the list, and out of scheduleIds on the next save.
                const list = (all || []).filter(s => s.isActive);
                setSchedules(list);
                const valid = new Set(list.map(s => s.scheduleId));
                setDraft(d => ({
                    ...d,
                    scheduleIds: (d.scheduleIds || []).filter(id => valid.has(id)),
                }));
            } catch (e) {
                console.error(e);
                if (!cancelled) setSchedules([]);
            } finally {
                if (!cancelled) setLoadingSch(false);
            }
        })();
        return () => { cancelled = true; };
    }, [draft.campaignId]);

    /** Re-read on every keystroke — the preview is the whole point. */
    const format = useMemo(
        () => (draft.fileNameExample?.trim() ? inferFileFormat(draft.fileNameExample) : null),
        [draft.fileNameExample]
    );

    /** When the next run is, and what it will be looking for. */
    const nextRun = useMemo(
        () => nextRunAt({ days: draft.days, startTime: draft.startTime, endTime: draft.endTime }),
        [draft.days, draft.startTime, draft.endTime]
    );

    const nextFile = useMemo(() => (
        format?.ok && nextRun
            ? renderFileName(format, nextRun, { time: draft.startTime })
            : ""
    ), [format, nextRun, draft.startTime]);

    const selectedCampaign = useMemo(
        () => campaigns.find(c => String(c.campaignId) === String(draft.campaignId)) || null,
        [campaigns, draft.campaignId]
    );

    /**
     * Days on which the chosen order is already spoken for.
     *
     * takenOrders arrives keyed by day — { mon: Set(1,3), tue: Set(1), … } —
     * because the order only has to be unique among automations that run on
     * the same day. Two Monday drops can't both be #1; a Monday drop and a
     * Tuesday drop can.
     */
    const orderClashDays = useMemo(() => {
        const order = Number(draft.uploadOrder);
        if (!order) return [];
        return (draft.days || []).filter(day => {
            const taken = takenOrders[day];
            if (!taken) return false;
            // An edit doesn't collide with its own existing row.
            const owned = isEdit && order === config?.uploadOrder && (config?.days || []).includes(day);
            return taken.has(order) && !owned;
        });
    }, [takenOrders, draft.uploadOrder, draft.days, isEdit, config]);

    /** 1…200, with the ones already taken on a selected day flagged. */
    const orderOptions = useMemo(() => {
        const out = [];
        for (let n = MIN_UPLOAD_ORDER; n <= MAX_UPLOAD_ORDER; n++) {
            const clash = (draft.days || []).filter(day => {
                const taken = takenOrders[day];
                if (!taken) return false;
                const owned = isEdit && n === config?.uploadOrder && (config?.days || []).includes(day);
                return taken.has(n) && !owned;
            });
            out.push({ value: n, clashDays: clash });
        }
        return out;
    }, [takenOrders, draft.days, isEdit, config]);

    const toggleSchedule = (id) => set({
        scheduleIds: draft.scheduleIds.includes(id)
            ? draft.scheduleIds.filter(s => s !== id)
            : [...draft.scheduleIds, id],
    });

    const allSelected = schedules.length > 0 && draft.scheduleIds.length === schedules.length;

    // ── Notification recipients ───────────────────────────────────
    // Same chip input as the report schedule dialog: a paste of several
    // addresses is split, and Backspace on an empty field removes the last.
    const addEmail = (raw) => {
        const parts = String(raw).split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
        if (!parts.length) return;

        const next = [...(draft.notifyRecipients || [])];
        let err = "";
        for (const p of parts) {
            if (!EMAIL_RE.test(p))                                   { err = AUTOMATION_STRINGS.INVALID_EMAIL;   continue; }
            if (next.some(e => e.toLowerCase() === p.toLowerCase())) { err = AUTOMATION_STRINGS.DUPLICATE_EMAIL; continue; }
            next.push(p);
        }
        set({ notifyRecipients: next });
        setEmailError(err);
        if (!err) setEmailInput("");
    };

    const removeEmail = (email) =>
        set({ notifyRecipients: (draft.notifyRecipients || []).filter(e => e !== email) });

    const handleEmailKeyDown = (e) => {
        if (["Enter", ",", ";", " ", "Tab"].includes(e.key) && emailInput.trim()) {
            if (e.key !== "Tab") e.preventDefault();
            addEmail(emailInput);
        } else if (e.key === "Backspace" && !emailInput && draft.notifyRecipients?.length) {
            removeEmail(draft.notifyRecipients[draft.notifyRecipients.length - 1]);
        }
    };

    // ── Save ──────────────────────────────────────────────────────
    const handleSave = () => {
        if (!draft.name.trim())                 return setFormError(AUTOMATION_STRINGS.REQUIRE_NAME);
        if (orderClashDays.length)              return setFormError(AUTOMATION_STRINGS.ORDER_TAKEN);
        if (!draft.campaignId)                  return setFormError(AUTOMATION_STRINGS.REQUIRE_CAMPAIGN);
        if (!draft.fileNameExample.trim())      return setFormError(AUTOMATION_STRINGS.REQUIRE_EXAMPLE);
        if (!format?.ok)                        return setFormError(format?.error || AUTOMATION_STRINGS.REQUIRE_EXAMPLE);
        if (!draft.days.length)                 return setFormError(AUTOMATION_STRINGS.REQUIRE_DAYS);
        if (!draft.startTime || !draft.endTime ||
            draft.endTime <= draft.startTime)   return setFormError(AUTOMATION_STRINGS.REQUIRE_WINDOW);
        if (!draft.scheduleIds.length)          return setFormError(AUTOMATION_STRINGS.REQUIRE_SCHEDULES);

        // Fold a half-typed address in rather than silently dropping it.
        let notifyRecipients = draft.notifyRecipients || [];
        if (emailInput.trim()) {
            const p = emailInput.trim();
            if (!EMAIL_RE.test(p)) { setEmailError(AUTOMATION_STRINGS.INVALID_EMAIL); return; }
            if (!notifyRecipients.some(e => e.toLowerCase() === p.toLowerCase())) {
                notifyRecipients = [...notifyRecipients, p];
            }
        }
        if (draft.notifyEnabled && !notifyRecipients.length) {
            return setFormError(AUTOMATION_STRINGS.REQUIRE_NOTIFY_TO);
        }

        setFormError("");
        setEmailInput("");
        onSave({
            ...draft,
            notifyRecipients,
            name: draft.name.trim(),
            description: (draft.description || "").trim(),
            campaignId: Number(draft.campaignId),
            uploadOrder: Number(draft.uploadOrder),
            fileNameExample: draft.fileNameExample.trim(),
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>

            <DialogTitle sx={{
                fontSize: 15, fontWeight: 500, py: 1.5,
                borderBottom: "0.5px solid", borderColor: "divider",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                {isEdit ? AUTOMATION_STRINGS.EDIT_CONFIG : AUTOMATION_STRINGS.ADD_CONFIG}
                <IconButton size="small" onClick={onClose} disabled={saving}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {formError && <Alert severity="warning" sx={{ mb: 2 }}>{formError}</Alert>}

                {/* Name */}
                <Field label="Name" required hint="Shown in the automation list.">
                    <TextField
                        fullWidth size="small"
                        placeholder="e.g. Morning DPD 30 drop — English"
                        value={draft.name}
                        onChange={(e) => set({ name: e.target.value })}
                        inputProps={{ maxLength: MAX_NAME, style: { fontSize: 13 } }}
                    />
                </Field>

                {/* Description */}
                <Field label="Description" hint="What this file carries and who owns it. Optional.">
                    <TextField
                        fullWidth size="small" multiline minRows={2} maxRows={4}
                        placeholder="e.g. First-touch accounts pulled at 6 AM, routed to the English reminder campaign."
                        value={draft.description}
                        onChange={(e) => set({ description: e.target.value })}
                        inputProps={{ maxLength: MAX_DESCRIPTION, style: { fontSize: 13 } }}
                    />
                    <Typography sx={{
                        fontSize: 11, color: "text.secondary", mt: 0.5, textAlign: "right",
                    }}>
                        {(draft.description || "").length}/{MAX_DESCRIPTION}
                    </Typography>
                </Field>

                {/* Language */}
                <Field label="Language" required hint="Filters the campaigns you can pick below.">
                    <Segmented
                        options={LANGUAGE_OPTIONS}
                        value={draft.language}
                        onChange={(v) => {
                            setTouchedCampaign(true);
                            set({ language: v });
                        }}
                    />
                </Field>

                {/* Upload order */}
                <Field
                    label="Upload order"
                    required
                    hint={`Which file runs first on a given day. 1–${MAX_UPLOAD_ORDER}; lower goes first. Only has to be unique among automations sharing a day.`}
                >
                    <Select
                        size="small"
                        value={Number(draft.uploadOrder) || MIN_UPLOAD_ORDER}
                        onChange={(e) => set({ uploadOrder: Number(e.target.value) })}
                        error={orderClashDays.length > 0}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                        sx={{ width: 130, fontSize: 13 }}
                    >
                        {orderOptions.map(o => (
                            <MenuItem
                                key={o.value}
                                value={o.value}
                                disabled={o.clashDays.length > 0}
                                sx={{ fontSize: 13 }}
                            >
                                {o.value}
                                {o.clashDays.length > 0 && (
                                    <Box component="span" sx={{ ml: 1, fontSize: 11, color: "text.disabled" }}>
                                        · taken {o.clashDays
                                            .map(d => DAY_OF_WEEK_OPTIONS.find(x => x.value === d)?.short || d)
                                            .join(", ")}
                                    </Box>
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                    {orderClashDays.length > 0 && (
                        <Typography sx={{ fontSize: 11, color: PINK, mt: 0.5 }}>
                            Already used on{" "}
                            {orderClashDays
                                .map(d => DAY_OF_WEEK_OPTIONS.find(o => o.value === d)?.short || d)
                                .join(", ")}
                            . Pick another number or change the days.
                        </Typography>
                    )}
                </Field>

                {/* Campaign */}
                <Field
                    label="Campaign"
                    required
                    hint={selectedCampaign?.description || undefined}
                >
                    <Select
                        fullWidth size="small" displayEmpty
                        value={draft.campaignId ?? ""}
                        onChange={(e) => set({ campaignId: e.target.value, scheduleIds: [] })}
                        disabled={loadingCamp}
                        sx={{ fontSize: 13 }}
                    >
                        <MenuItem value="" disabled sx={{ fontSize: 13 }}>
                            {loadingCamp ? "Loading campaigns…" : "Select a campaign"}
                        </MenuItem>
                        {campaigns.map(c => (
                            <MenuItem key={c.campaignId} value={c.campaignId} sx={{ fontSize: 13 }}>
                                {c.campaignName}
                            </MenuItem>
                        ))}
                    </Select>
                    {!loadingCamp && campaigns.length === 0 && (
                        <Typography sx={{ fontSize: 11, color: AMBER, mt: 0.5 }}>
                            {AUTOMATION_STRINGS.NO_CAMPAIGNS}
                        </Typography>
                    )}
                </Field>

                {/* Schedules inside the campaign */}
                <Field label="Schedules" required hint={AUTOMATION_STRINGS.SCHEDULES_HINT}>
                    {loadingSch ? (
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading schedules…</Typography>
                        </Box>
                    ) : !draft.campaignId ? (
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            Pick a campaign first.
                        </Typography>
                    ) : schedules.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: AMBER }}>
                            {AUTOMATION_STRINGS.NO_SCHEDULES}
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", mb: 0.5,
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                                    {draft.scheduleIds.length} of {schedules.length} selected
                                </Typography>
                                <Box
                                    onClick={() => set({
                                        scheduleIds: allSelected ? [] : schedules.map(s => s.scheduleId),
                                    })}
                                    sx={{
                                        fontSize: 11, color: BLUE, cursor: "pointer", userSelect: "none",
                                        "&:hover": { textDecoration: "underline" },
                                    }}
                                >
                                    {allSelected ? "Clear all" : "Select all"}
                                </Box>
                            </Box>

                            <Box sx={{
                                border: "0.5px solid", borderColor: "divider", borderRadius: 1,
                                maxHeight: 220, overflowY: "auto",
                            }}>
                                {schedules.map((s, i) => {
                                    const on = draft.scheduleIds.includes(s.scheduleId);
                                    return (
                                        <Box
                                            key={s.scheduleId}
                                            onClick={() => toggleSchedule(s.scheduleId)}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 1,
                                                px: 1, py: "6px", cursor: "pointer",
                                                borderTop: i === 0 ? "none" : "0.5px solid",
                                                borderColor: "divider",
                                                background: on ? "rgba(55,138,221,0.06)" : "transparent",
                                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                                            }}
                                        >
                                            <Checkbox
                                                size="small" checked={on}
                                                sx={{ p: 0.5, color: BLUE, "&.Mui-checked": { color: BLUE } }}
                                            />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Box sx={{
                                                    fontSize: 12, color: "text.primary",
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                }}>
                                                    {s.scheduleName || `Schedule ${s.scheduleId}`}
                                                </Box>
                                                <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                                                    {scheduleHint(s)}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Field>

                {/* Days */}
                <Field label="Days" required hint="Days this file is expected to land.">
                    <DayPicker value={draft.days} onChange={(v) => set({ days: v })} />
                </Field>

                {/* Time range — in the client zone */}
                <Field label="Time range" required>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <TextField
                            size="small" type="time"
                            value={draft.startTime}
                            onChange={(e) => set({ startTime: e.target.value })}
                            inputProps={{ step: 300, style: { fontSize: 13 } }}
                            sx={{ width: 130 }}
                        />
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>to</Typography>
                        <TextField
                            size="small" type="time"
                            value={draft.endTime}
                            onChange={(e) => set({ endTime: e.target.value })}
                            inputProps={{ step: 300, style: { fontSize: 13 } }}
                            sx={{ width: 130 }}
                        />
                        <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            px: "10px", py: "5px", borderRadius: "20px",
                            border: "0.5px solid", borderColor: "divider",
                            background: "#f7f7f7", fontSize: 12, color: "text.secondary",
                        }}>
                            <ScheduleIcon sx={{ fontSize: 14, color: BLUE }} />
                            {tz.name} ({tz.key})
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                        {AUTOMATION_STRINGS.TZ_SOURCE}
                    </Typography>
                </Field>

                {/* File format */}
                <Field
                    label="File name"
                    required
                    hint={AUTOMATION_STRINGS.FILE_EXAMPLE_HINT}
                >
                    <TextField
                        fullWidth size="small"
                        placeholder="e.g. VEROS_DPD30_20260803_01.csv"
                        value={draft.fileNameExample}
                        onChange={(e) => set({ fileNameExample: e.target.value })}
                        inputProps={{
                            maxLength: MAX_FILE_EXAMPLE,
                            style: { fontSize: 13, fontFamily: MONO },
                        }}
                    />

                    {format && !format.ok && (
                        <Typography sx={{ fontSize: 11, color: PINK, mt: 0.5 }}>
                            {format.error}
                        </Typography>
                    )}

                    {format?.ok && (
                        <Box sx={{
                            mt: 1, border: "0.5px solid", borderColor: "divider",
                            borderRadius: 1, overflow: "hidden",
                        }}>
                            {/* Detected pattern */}
                            <Box sx={{ px: 1.5, py: 1, background: "#f7f7f7" }}>
                                <Typography sx={{
                                    fontSize: 10, fontWeight: 500, color: "text.secondary",
                                    textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5,
                                }}>
                                    Detected pattern
                                </Typography>
                                <Box sx={{ fontSize: 12, fontFamily: MONO, color: "text.primary", wordBreak: "break-all" }}>
                                    {highlightTemplate(format.template)}
                                </Box>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                                    {describeFormat(format)}
                                </Typography>
                            </Box>

                            {/* Next expected file */}
                            <Box sx={{ px: 1.5, py: 1, borderTop: "0.5px solid", borderColor: "divider" }}>
                                <Typography sx={{
                                    fontSize: 10, fontWeight: 500, color: "text.secondary",
                                    textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5,
                                }}>
                                    {AUTOMATION_STRINGS.NEXT_FILE}
                                </Typography>
                                {nextRun ? (
                                    <>
                                        <Box sx={{
                                            fontSize: 12, fontFamily: MONO, color: GREEN, wordBreak: "break-all",
                                        }}>
                                            {nextFile}
                                        </Box>
                                        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                                            {nextRun.toLocaleDateString(undefined, RUN_DATE_FMT)} from{" "}
                                            {formatClock(draft.startTime)} ({tz.key})
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography sx={{ fontSize: 11, color: AMBER }}>
                                        Pick at least one day to see the next file.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </Field>

                {/* Notifications */}
                <Field label="Notifications" hint={AUTOMATION_STRINGS.NOTIFY_HINT}>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={!!draft.notifyEnabled}
                                onChange={(e) => set({ notifyEnabled: e.target.checked })}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 13 }}>
                                {draft.notifyEnabled ? "Email on every run" : "No email"}
                            </Typography>
                        }
                    />

                    {draft.notifyEnabled && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{
                                border: "0.5px solid", borderColor: emailError ? PINK : "divider",
                                borderRadius: 1, p: 1,
                                display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center",
                                minHeight: 42,
                                "&:focus-within": { borderColor: BLUE },
                            }}>
                                {(draft.notifyRecipients || []).map(email => (
                                    <Chip
                                        key={email}
                                        label={email}
                                        size="small"
                                        onDelete={() => removeEmail(email)}
                                        sx={{ fontSize: 12, height: 24, borderRadius: "12px" }}
                                    />
                                ))}
                                <Box
                                    component="input"
                                    value={emailInput}
                                    placeholder={draft.notifyRecipients?.length ? "" : "name@company.com"}
                                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                                    onKeyDown={handleEmailKeyDown}
                                    onBlur={() => emailInput.trim() && addEmail(emailInput)}
                                    sx={{
                                        flex: 1, minWidth: 160, border: "none", outline: "none",
                                        fontSize: 13, background: "transparent",
                                        fontFamily: "inherit", color: "inherit", p: "2px",
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: 11, color: emailError ? PINK : "text.secondary", mt: 0.5 }}>
                                {emailError || AUTOMATION_STRINGS.NOTIFY_RECIPIENTS_HINT}
                            </Typography>
                        </Box>
                    )}
                </Field>

                {/* Active */}
                <Field label="Status">
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={!!draft.isEnabled}
                                onChange={(e) => set({ isEnabled: e.target.checked })}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 13 }}>
                                {draft.isEnabled ? "Active — files route on schedule" : "Paused — files are ignored"}
                            </Typography>
                        }
                    />
                </Field>

                {/* Plain-language recap of everything above */}
                <Box sx={{
                    background: "rgba(55,138,221,0.06)", borderRadius: 2,
                    p: "10px 14px", mb: 1,
                }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {describeConfig(draft, tzKey, selectedCampaign?.campaignName)} ·{" "}
                        {draft.scheduleIds.length || "no"} schedule{draft.scheduleIds.length === 1 ? "" : "s"}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                px: 3, py: 2, gap: 1,
                borderTop: "0.5px solid", borderColor: "divider",
            }}>
                <Box onClick={saving ? undefined : onClose} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    color: "text.secondary", cursor: saving ? "default" : "pointer",
                    userSelect: "none",
                }}>Cancel</Box>

                <Box onClick={saving ? undefined : handleSave} sx={{
                    px: "18px", py: "6px", fontSize: 13, borderRadius: "20px",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    cursor: saving ? "default" : "pointer", userSelect: "none",
                    opacity: saving ? 0.6 : 1,
                    display: "inline-flex", alignItems: "center", gap: 1,
                    "&:hover": saving ? {} : { background: "#2a6db5" },
                }}>
                    {saving && <CircularProgress size={13} sx={{ color: "#fff" }} />}
                    {isEdit ? "Save changes" : "Create automation"}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UploadAutomationDialog;